import React, { useEffect, useMemo, useRef, useState } from 'react';
import { geoNaturalEarth1, geoPath } from 'd3-geo';
import { Card } from '../ui';
import { LEVELS, LEVEL_COLORS, NONE, ScoreBadge, TFlag } from './parts';

export { LEVEL_COLORS } from './parts';
const ALIAS = { KOS: 'XKX', SDS: 'SSD', PSX: 'PSE' };
const W = 960;
const H = 500;

function Legend() {
  return (
    <div className="flex flex-wrap gap-x-5 gap-y-2 mt-4" data-testid="travel-map-legend">
      {[1, 2, 3, 4].map((l) => (
        <span key={l} className="flex items-center gap-2 text-xs text-[#8A8F97]"><i className="w-3 h-3 rounded-sm" style={{ background: LEVEL_COLORS[l] }} />{LEVELS[l]}</span>
      ))}
      <span className="flex items-center gap-2 text-xs text-[#8A8F97]"><i className="w-3 h-3 rounded-sm" style={{ background: NONE }} />Nessuna scheda</span>
    </div>
  );
}

function Tip({ tip }) {
  if (!tip) return null;
  const { c, x, y } = tip;
  return (
    <div className="absolute z-10 pointer-events-none bg-[#0B0D10]/95 backdrop-blur border border-[#1F242C] rounded-sm px-3 py-2 w-56 shadow-xl" style={{ left: x, top: y }} data-testid="travel-map-tooltip">
      <p className="text-sm font-semibold text-[#F2F2F2] flex items-center gap-2"><TFlag iso2={c.iso2} className="w-5 h-3.5" />{c.name}</p>
      <p className="text-xs mt-1 flex items-center gap-2" style={{ color: LEVEL_COLORS[c.farnesina_level] || '#8A8F97' }}>
        <i className="w-2 h-2 rounded-full" style={{ background: LEVEL_COLORS[c.farnesina_level] || NONE }} />{LEVELS[c.farnesina_level] || 'Livello non disponibile'}
      </p>
      <div className="flex items-center justify-between mt-2 text-xs text-[#8A8F97]"><span>Indice BOND</span><ScoreBadge score={c.score == null ? null : Math.round(c.score)} /></div>
      {c.news > 0 && <p className="text-[11px] text-[#E37A72] mt-1">{c.news} avvisi dell'ultima ora</p>}
      <p className="text-[10px] text-[#5A5F67] mt-2">Clicca per aprire la scheda</p>
    </div>
  );
}

export default function WorldMap({ countries, onOpen }) {
  const [geo, setGeo] = useState(null);
  const [tip, setTip] = useState(null);
  const [t, setT] = useState({ k: 1, x: 0, y: 0 });
  const box = useRef(null);
  const svg = useRef(null);
  const drag = useRef(null);
  const zoomAt = (factor, px = W / 2, py = H / 2) => setT((p) => {
    const k = Math.min(16, Math.max(1, p.k * factor));
    if (k === 1) return { k: 1, x: 0, y: 0 };
    return { k, x: px - ((px - p.x) * k) / p.k, y: py - ((py - p.y) * k) / p.k };
  });
  const toView = (e) => { const r = svg.current.getBoundingClientRect(); return [((e.clientX - r.left) * W) / r.width, ((e.clientY - r.top) * H) / r.height]; };
  useEffect(() => {
    const el = svg.current;
    if (!el) return undefined;
    const onWheel = (e) => { e.preventDefault(); const [px, py] = toView(e); zoomAt(e.deltaY < 0 ? 1.3 : 1 / 1.3, px, py); };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [geo]); // eslint-disable-line react-hooks/exhaustive-deps
  const down = (e) => { drag.current = { sx: e.clientX, sy: e.clientY, x: t.x, y: t.y, moved: false }; };
  const pan = (e) => {
    const d = drag.current;
    if (!d || t.k === 1) return;
    const r = svg.current.getBoundingClientRect();
    const dx = ((e.clientX - d.sx) * W) / r.width;
    const dy = ((e.clientY - d.sy) * H) / r.height;
    if (Math.abs(dx) + Math.abs(dy) > 4) { d.moved = true; setTip(null); }
    setT((p) => ({ ...p, x: d.x + dx, y: d.y + dy }));
  };
  const up = () => { setTimeout(() => { drag.current = null; }, 0); };
  const open = (iso3) => { if (!drag.current?.moved) onOpen(iso3); };
  useEffect(() => { fetch('/geo/world.json').then((r) => r.json()).then(setGeo).catch(() => setGeo({ features: [] })); }, []);
  const byIso = useMemo(() => Object.fromEntries(countries.map((c) => [c.iso3, c])), [countries]);
  const path = useMemo(() => (geo ? geoPath(geoNaturalEarth1().fitSize([W, H], geo)) : null), [geo]);
  const move = (e, c) => {
    const r = box.current.getBoundingClientRect();
    const x = e.clientX - r.left;
    setTip({ c, x: Math.min(x + 14, r.width - 230), y: e.clientY - r.top + 14 });
  };
  return (
    <Card className="p-6 mb-4" data-testid="travel-map">
      <div className="flex items-center justify-between gap-3 mb-4">
        <p className="text-[11px] uppercase tracking-[0.18em] text-[#D4AF37]">Mappa del rischio Paese · indicazione Farnesina</p>
        <div className="flex items-center gap-1" data-testid="travel-map-zoom">
          {[['−', () => zoomAt(1 / 1.6), 'out'], ['+', () => zoomAt(1.6), 'in'], ['↺', () => setT({ k: 1, x: 0, y: 0 }), 'reset']].map(([l, fn, id]) => (
            <button key={id} onClick={fn} className="w-8 h-8 grid place-items-center border border-[#1F242C] rounded-sm text-[#C9CCD1] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors" aria-label={`zoom-${id}`} data-testid={`travel-map-zoom-${id}`}>{l}</button>
          ))}
        </div>
      </div>
      <div ref={box} className="relative overflow-hidden rounded-sm" onMouseLeave={() => { setTip(null); up(); }}>
        <svg ref={svg} viewBox={`0 0 ${W} ${H}`} className={`w-full h-auto select-none ${t.k > 1 ? 'cursor-grab active:cursor-grabbing' : ''}`} role="img" aria-label="Mappa del mondo per livello di rischio"
          onPointerDown={down} onPointerMove={pan} onPointerUp={up} style={{ touchAction: t.k > 1 ? 'none' : 'auto' }}>
          <g transform={`translate(${t.x},${t.y}) scale(${t.k})`}>
          {path && geo.features.map((f) => {
            const c = byIso[f.id] || byIso[ALIAS[f.id]];
            return (
              <path key={f.id} d={path(f)} fill={LEVEL_COLORS[c?.farnesina_level] || NONE} stroke="#0B0D10" strokeWidth={0.5 / t.k}
                className={`transition-opacity duration-150 ${c ? 'cursor-pointer hover:opacity-75' : ''}`}
                onMouseMove={c ? (e) => !drag.current?.moved && move(e, c) : undefined} onMouseLeave={() => setTip(null)} onClick={c ? () => open(c.iso3) : undefined}
                data-testid={`travel-map-country-${f.id}`} />
            );
          })}
          </g>
        </svg>
        <p className="absolute bottom-2 left-2 text-[10px] text-[#8A8F97] bg-[#0B0D10]/70 px-2 py-0.5 rounded-sm pointer-events-none">Rotella o +/− per ingrandire · trascina per spostarti</p>
        <Tip tip={tip} />
      </div>
      <Legend />
    </Card>
  );
}
